import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Separator } from '../../components/ui/separator';
import { api } from '../../context/AuthContext';
import { AdminPageHeader, AdminKpi, AdminKpiStrip } from '../../components/admin/AdminPageHeader';
import AdminToolbar from '../../components/admin/AdminToolbar';
import { cn } from '../../lib/utils';
import { 
  Calculator, Plus, CurrencyEur, CheckCircle, Clock, Warning, Users,
  Calendar, CaretRight, PencilSimple, Eye, Bank, Wallet, ArrowRight,
  ChartPie, ListBullets, Receipt, Coins, X, FloppyDisk, Play, Camera,
  CreditCard, Check, ArrowClockwise
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const distributionStatusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: PencilSimple },
  declared: { label: 'Declared', color: 'bg-blue-100 text-blue-700', icon: Eye },
  record_date_set: { label: 'Snapshot Taken', color: 'bg-indigo-100 text-indigo-700', icon: Camera },
  calculated: { label: 'Calculated', color: 'bg-purple-100 text-purple-700', icon: Calculator },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  partially_paid: { label: 'Partially Paid', color: 'bg-amber-100 text-amber-700', icon: CreditCard },
  fully_paid: { label: 'Fully Paid', color: 'bg-emerald-100 text-emerald-700', icon: Check },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: X },
};

const distributionTypeConfig = {
  dividend: { label: 'Dividend', description: 'Regular income distribution' },
  return_of_capital: { label: 'Return of Capital', description: 'Principal return' },
  liquidation: { label: 'Liquidation', description: 'Final distribution' },
  special: { label: 'Special', description: 'One-time special distribution' },
};

const entitlementStatusConfig = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700' },
  on_hold: { label: 'On Hold', color: 'bg-yellow-100 text-yellow-700' },
  payable: { label: 'Payable', color: 'bg-blue-100 text-blue-700' },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' },
};

/**
 * Distribution Card
 */
const DistributionCard = ({ distribution, onClick, onDelete }) => {
  const status = distributionStatusConfig[distribution.status] || distributionStatusConfig.draft;
  const type = distributionTypeConfig[distribution.distribution_type] || distributionTypeConfig.dividend;
  const StatusIcon = status.icon;
  
  const paymentProgress = parseFloat(distribution.total_amount) > 0
    ? (parseFloat(distribution.total_paid || 0) / parseFloat(distribution.total_amount)) * 100
    : 0;

  return (
    <Card 
      className="bg-white rounded-xl border border-black/[0.04] p-5 cursor-pointer hover:border-primary/20 hover:shadow-md transition-all"
      onClick={() => onClick(distribution)}
      data-testid={`distribution-card-${distribution.code}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs text-foreground-muted">{distribution.code}</span>
            <Badge className={cn('text-xs', status.color)}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {status.label}
            </Badge>
          </div>
          <h3 className="font-semibold text-foreground">{distribution.description || type.label}</h3>
          <Badge variant="outline" className="text-xs mt-1">{type.label}</Badge>
          <p className="text-xs text-foreground-muted mt-2" data-testid={`distribution-context-${distribution.code}`}>
            {distribution.project_title || 'Project unknown'}
            {distribution.project_code ? ` · ${distribution.project_code}` : ''}
          </p>
          <p className="text-xs text-foreground-muted">
            {distribution.instrument_name || 'Instrument unknown'}
            {distribution.instrument_code ? ` · ${distribution.instrument_code}` : ''}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xl font-bold text-primary">€{parseFloat(distribution.total_amount).toLocaleString('en-GB', { maximumFractionDigits: 2 })}</p>
          <p className="text-xs text-foreground-muted">Total Amount</p>
          <Button
            variant="ghost" size="sm"
            className="mt-2 h-7 px-2 text-xs text-foreground-muted hover:text-red-600"
            onClick={(e) => { e.stopPropagation(); onDelete(distribution); }}
            data-testid={`delete-distribution-${distribution.code}`}
          >
            Delete & reverse
          </Button>
        </div>
      </div>
      
      {/* Payment Progress */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-foreground-muted">Payment Progress</span>
          <span className="font-medium">{paymentProgress.toFixed(0)}%</span>
        </div>
        <Progress value={paymentProgress} className="h-2" />
        <div className="flex justify-between text-xs text-foreground-muted mt-1">
          <span>€{parseFloat(distribution.total_paid || 0).toLocaleString()} paid</span>
          <span>€{parseFloat(distribution.total_pending || 0).toLocaleString()} pending</span>
        </div>
      </div>
      
      {/* Key Info */}
      <div className="grid grid-cols-3 gap-2 text-sm mb-3 p-2 bg-slate-50 rounded-lg">
        <div className="text-center">
          <p className="font-bold">{distribution.holders_count || 0}</p>
          <p className="text-xs text-foreground-muted">Holders</p>
        </div>
        <div className="text-center border-x border-black/[0.04]">
          <p className="font-bold">{distribution.units_eligible?.toLocaleString() || '—'}</p>
          <p className="text-xs text-foreground-muted">Units</p>
        </div>
        <div className="text-center">
          <p className="font-bold">
            {distribution.per_unit_amount
              ? `€${parseFloat(distribution.per_unit_amount).toLocaleString('en-GB', { maximumFractionDigits: 4 })}`
              : '—'}
          </p>
          <p className="text-xs text-foreground-muted">Per Unit</p>
        </div>
      </div>
      
      <div className="flex items-center justify-between pt-3 border-t border-black/[0.04]">
        <span className="text-xs text-foreground-muted">
          Record: {distribution.record_date || '—'}
        </span>
        <div className="flex items-center gap-1 text-sm text-primary">
          <span>Open wizard</span>
          <CaretRight className="h-4 w-4" />
        </div>
      </div>
    </Card>
  );
};

/**
 * Amount straight from the project's actual financials, rather than typed in.
 * Closing income only appears once a project is exiting or exited; operating income is offered
 * month by month from the operating phase onwards.
 */
const AmountFromFinancials = ({ projectId, onPick }) => {
  const [options, setOptions] = useState(null);
  const [month, setMonth] = useState('');

  useEffect(() => {
    setOptions(null);
    setMonth('');
    if (!projectId) return;
    api.get(`/admin/v2/distributions/suggested-amounts?project_id=${projectId}`)
      .then((res) => setOptions(res.data))
      .catch(() => setOptions({ closing_income: null, operating_months: [] }));
  }, [projectId]);

  if (!projectId) return null;

  const closing = options?.closing_income;
  const months = options?.operating_months || [];
  const picked = months.find((m) => m.month === month);

  return (
    <div className="rounded-xl border border-black/[0.06] bg-slate-50 p-4 space-y-3" data-testid="amount-from-financials">
      <p className="text-xs uppercase tracking-[0.14em] text-foreground-muted">Or take the amount from financials</p>

      {closing ? (
        <Button
          type="button" variant="outline" size="sm" className="w-full justify-between"
          onClick={() => onPick(closing.amount, 'Closing income distribution')}
          data-testid="use-closing-income"
        >
          <span>Actual closing income</span>
          <span className="tabular-nums font-medium">€{closing.amount.toLocaleString('en-GB')}</span>
        </Button>
      ) : (
        <p className="text-xs text-foreground-muted" data-testid="closing-income-unavailable">
          {options?.closing_income_reason || 'No actual closing income recorded yet'}
        </p>
      )}

      {months.length > 0 ? (
        <div className="flex items-center gap-2">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="flex-1" data-testid="operating-month-select">
              <SelectValue placeholder="Operating income for a month" />
            </SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m.month} value={m.month}>
                  {m.month} · €{m.amount.toLocaleString('en-GB')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button" variant="outline" size="sm" disabled={!picked}
            onClick={() => onPick(picked.amount, `Operating income for ${picked.month}`)}
            data-testid="use-operating-income"
          >
            Use
          </Button>
        </div>
      ) : (
        <p className="text-xs text-foreground-muted" data-testid="operating-income-unavailable">
          {options?.operating_reason || 'No positive operating month recorded yet'}
        </p>
      )}
    </div>
  );
};

/**
 * Create Distribution Dialog
 */
const CreateDistributionDialog = ({ open, onOpenChange, securities, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    security_id: '',
    distribution_type: 'dividend',
    total_amount: '',
    record_date: '',
    payment_date: '',
    description: '',
  });

  useEffect(() => {
    if (open) {
      setFormData({
        security_id: (securities.find((s) => s.can_distribute) || {}).id || '',
        distribution_type: 'dividend',
        total_amount: '',
        record_date: new Date().toISOString().split('T')[0],
        payment_date: '',
        description: '',
      });
    }
  }, [open, securities]);

  const selectedSecurity = securities.find((sec) => sec.id === formData.security_id);

  const handleSubmit = async () => {
    if (!formData.security_id) {
      toast.error('Please select a security');
      return;
    }
    if (!formData.total_amount || parseFloat(formData.total_amount) <= 0) {
      toast.error('Please enter a valid total amount');
      return;
    }
    if (!formData.record_date) {
      toast.error('Please select a record date');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/admin/v2/distributions', {
        security_id: formData.security_id,
        distribution_type: formData.distribution_type,
        total_amount: formData.total_amount,
        record_date: formData.record_date,
        payment_date: formData.payment_date || null,
        description: formData.description || null,
      });
      toast.success('Distribution created - let us walk through it');
      onSave(res.data.distribution || res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create distribution');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Distribution</DialogTitle>
          <DialogDescription>
            Create a new distribution for security holders.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Security *</Label>
            <Select value={formData.security_id} onValueChange={(v) => setFormData({...formData, security_id: v})}>
              <SelectTrigger data-testid="security-select">
                <SelectValue placeholder="Select security" />
              </SelectTrigger>
              <SelectContent>
                {securities.filter((sec) => sec.can_distribute).map(sec => (
                  <SelectItem key={sec.id} value={sec.id}>
                    {sec.code} - {sec.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Distribution Type</Label>
              <Select value={formData.distribution_type} onValueChange={(v) => setFormData({...formData, distribution_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(distributionTypeConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Total Amount (€) *</Label>
              <Input
                type="number"
                value={formData.total_amount}
                onChange={(e) => setFormData({...formData, total_amount: e.target.value})}
                placeholder="10000"
                data-testid="amount-input"
              />
            </div>
          </div>

          <AmountFromFinancials
            projectId={selectedSecurity?.project_id}
            onPick={(amount, note) => setFormData((f) => ({
              ...f,
              total_amount: String(amount),
              // The description follows the source unless the admin wrote their own wording
              description: f.description && !f.description.endsWith('distribution') ? f.description : note,
            }))}
          />

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Record Date *</Label>
              <Input
                type="date"
                value={formData.record_date}
                onChange={(e) => setFormData({...formData, record_date: e.target.value})}
                data-testid="record-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={formData.payment_date}
                onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Q4 2026 Dividend Distribution"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} data-testid="create-distribution-btn">
            {loading ? 'Creating...' : 'Create Distribution'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Main Distribution Calculator Page
 */
export default function AdminDistributionCalculatorPage() {
  const navigate = useNavigate();
  
  const [distributions, setDistributions] = useState([]);
  const [securities, setSecurities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('newest');
  const [viewMode, setViewMode] = useState('card');
  
  const [createOpen, setCreateOpen] = useState(false);

  const handleDelete = async (distribution) => {
    const paid = parseFloat(distribution.total_paid || 0);
    const confirmed = window.confirm(
      `Delete ${distribution.code} and reverse it?\n\n` +
      (paid > 0
        ? `€${paid.toLocaleString('en-GB')} already recorded as paid will be reversed and the payment records removed. `
        : '') +
      `Every entitlement for this distribution is removed. This cannot be undone.`
    );
    if (!confirmed) return;
    try {
      const res = await api.delete(`/admin/v2/distributions/${distribution.id}`);
      toast.success(res.data?.message || 'Distribution deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete distribution');
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      
      const [distRes, secRes, statsRes] = await Promise.all([
        api.get(`/admin/v2/distributions?${params}`),
        api.get('/admin/v2/distributions/eligible-securities'),
        api.get('/admin/v2/distributions/stats').catch(() => ({ data: null }))
      ]);
      
      setDistributions(distRes.data.distributions || []);
      setSecurities(secRes.data.securities || []);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load distributions');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openWizard = (distribution) => navigate(`/admin/v2/distributions/${distribution.id}`);

  const eligible = securities.filter((s) => s.can_distribute);

  const visible = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = distributions.filter((d) => {
      if (q && ![d.code, d.description, d.distribution_type].filter(Boolean).join(' ').toLowerCase().includes(q)) return false;
      if (filterType !== 'all' && d.distribution_type !== filterType) return false;
      return true;
    });
    if (sort === 'amount') list = [...list].sort((a, b) => Number(b.total_amount || 0) - Number(a.total_amount || 0));
    return list;
  }, [distributions, search, filterType, sort]);


  return (
    <div className="max-w-[1200px] space-y-6" data-testid="admin-distribution-calculator-page">
      <AdminPageHeader
        eyebrow="Capital"
        title="Distributions"
        subtitle="Declare, calculate and pay investor distributions per instrument."
        actions={(
          <Button
            size="sm"
            onClick={() => setCreateOpen(true)}
            disabled={eligible.length === 0}
            data-testid="create-distribution-btn"
          >
            <Plus className="h-4 w-4 md:mr-1.5" />
            <span className="hidden md:inline">New distribution</span>
          </Button>
        )}
      />

      {stats && (
        <AdminKpiStrip>
          <AdminKpi label="Distributions" value={stats.total || distributions.length} />
          <AdminKpi label="Pending" value={stats.pending || 0} />
          <AdminKpi label="Ready to pay" value={stats.ready_to_pay || 0} />
          <AdminKpi label="Declared" value={`€${((stats.total_declared || 0) / 1000).toFixed(0)}K`} />
          <AdminKpi label="Paid" value={`€${((stats.total_paid || 0) / 1000).toFixed(0)}K`} />
        </AdminKpiStrip>
      )}


      <AdminToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search distributions by code or description…"
        filters={[
          { key: 'status', label: 'Status', options: Object.entries(distributionStatusConfig).map(([value, c]) => ({ value, label: c.label })) },
          { key: 'type', label: 'Type', options: Object.entries(distributionTypeConfig).map(([value, c]) => ({ value, label: c.label })) },
        ]}
        values={{ status: filterStatus, type: filterType }}
        onFilter={(key, value) => (key === 'status' ? setFilterStatus(value) : setFilterType(value))}
        onClear={() => { setFilterStatus('all'); setFilterType('all'); }}
        sortOptions={[
          { value: 'newest', label: 'Newest first' },
          { value: 'amount', label: 'Largest amount' },
        ]}
        sort={sort}
        onSort={setSort}
        view={viewMode}
        onView={setViewMode}
        resultLabel={`${visible.length} of ${distributions.length} distributions`}
      />

      {/* Distributions List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary" />
        </div>
      ) : (
        <>
          {eligible.length === 0 && (
            <Card className="bg-white p-6 rounded-xl border border-black/[0.04]" data-testid="no-eligible-instruments">
              <div className="flex items-start gap-3">
                <Coins className="h-5 w-5 text-foreground-muted mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium">No instrument can distribute right now</h3>
                  <p className="text-sm text-foreground-muted mt-1">
                    Distributions open up once investors hold units, the offering is closed or fully funded, and the
                    project is Funded, In execution, Operating or Exiting. Once a project exits, its instrument is
                    archived and no further distributions can be created.
                  </p>
                </div>
              </div>
              {securities.length > 0 && (
                <div className="mt-4 divide-y divide-black/[0.05] border-t border-black/[0.05]">
                  {securities.map((sec) => (
                    <div key={sec.id} className="py-2.5 flex items-start justify-between gap-4 text-sm">
                      <span className="min-w-0">
                        <span className="font-medium">{sec.name || sec.code}</span>
                        <span className="block text-xs text-foreground-muted">{sec.project_title}</span>
                      </span>
                      <span className="text-xs text-amber-700 text-right max-w-[420px]">{sec.blocked_reason}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {visible.length === 0 ? (
            <Card className="bg-white p-12 text-center rounded-xl border border-black/[0.04]">
              <Calculator className="h-12 w-12 text-foreground-muted mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No distributions yet</h3>
              <p className="text-foreground-muted mb-4">
                {filterStatus !== 'all' ? 'Try adjusting your filters' : 'Create your first distribution to get started'}
              </p>
              <Button onClick={() => setCreateOpen(true)} disabled={eligible.length === 0}>
                <Plus className="h-4 w-4 mr-2" />
                New distribution
              </Button>
            </Card>
          ) : viewMode === 'list' ? (
            <Card className="bg-white rounded-xl border border-black/[0.04] overflow-hidden" data-testid="distributions-list-view">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-black/[0.08] bg-slate-50">
                      <th className="text-left py-2.5 px-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Distribution</th>
                      <th className="text-left py-2.5 px-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Status</th>
                      <th className="text-right py-2.5 px-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Amount</th>
                      <th className="text-right py-2.5 px-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Per unit</th>
                      <th className="text-center py-2.5 px-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Holders</th>
                      <th className="text-right py-2.5 px-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Record date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((d) => (
                      <tr
                        key={d.id}
                        onClick={() => openWizard(d)}
                        className="border-b border-black/[0.04] hover:bg-slate-50 cursor-pointer transition-colors"
                        data-testid={`distribution-row-${d.code}`}
                      >
                        <td className="py-3 px-4">
                          <span className="font-mono text-[10px] text-foreground-muted block">{d.code}</span>
                          <span className="text-sm font-medium">{d.description || d.distribution_type}</span>
                        </td>
                        <td className="py-3 px-4 text-sm capitalize">{(d.status || '').replace(/_/g, ' ')}</td>
                        <td className="py-3 px-4 text-right text-sm tabular-nums">
                          €{parseFloat(d.total_amount || 0).toLocaleString('en-GB', { maximumFractionDigits: 2 })}
                        </td>
                        <td className="py-3 px-4 text-right text-sm tabular-nums">
                          {d.per_unit_amount ? `€${parseFloat(d.per_unit_amount).toLocaleString('en-GB', { maximumFractionDigits: 4 })}` : '—'}
                        </td>
                        <td className="py-3 px-4 text-center text-sm">{d.holders_count || '—'}</td>
                        <td className="py-3 px-4 text-right text-xs text-foreground-muted">{d.record_date || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {visible.map((distribution) => (
                <DistributionCard
                  key={distribution.id}
                  distribution={distribution}
                  onClick={openWizard}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </>
      )}

      <CreateDistributionDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        securities={securities}
        onSave={(created) => {
          fetchData();
          if (created?.id) navigate(`/admin/v2/distributions/${created.id}`);
        }}
      />
    </div>
  );
}
